import { TrafficActionType } from "../models/enums";

const MIN_STATION_COUNT_BY_TEMPLATE: Partial<Record<TrafficActionType, number>> =
  {
    [TrafficActionType.BrokenTrip]: 2,
    [TrafficActionType.CancelledBreak]: 1,
  };

export const NO_TRIPS_FOUND_MESSAGE = "Inga resor hittades från filtret.";

export function resolveTrafficActionType(
  templateType: TrafficActionType | string | number | null | undefined
): TrafficActionType {
  if (templateType == null) {
    return TrafficActionType.UNDEFINED;
  }

  if (typeof templateType === "string") {
    const numericValue = Number(templateType);
    if (!Number.isNaN(numericValue) && numericValue in TrafficActionType) {
      return numericValue as TrafficActionType;
    }

    const enumValue =
      TrafficActionType[templateType as keyof typeof TrafficActionType];
    if (typeof enumValue === "number") {
      return enumValue;
    }

    return TrafficActionType.UNDEFINED;
  }

  return templateType;
}

export function getMinimumStationCountForTemplate(
  templateType: TrafficActionType | string | number | null | undefined
): number {
  const type = resolveTrafficActionType(templateType);
  return MIN_STATION_COUNT_BY_TEMPLATE[type] ?? 0;
}

export function templateRequiresTrainTrips(
  templateType: TrafficActionType | string | number | null | undefined
): boolean {
  const type = resolveTrafficActionType(templateType);
  return (
    type !== TrafficActionType.OtherAdjustment &&
    type !== TrafficActionType.UNDEFINED
  );
}

export function canGenerateFromTemplate(
  templateType: TrafficActionType | string | number | null | undefined,
  stationCodes: string[] | null | undefined,
  trains: { length: number } | null | undefined = []
): boolean {
  return (
    getTemplateGenerationRequirementMessage(
      templateType,
      stationCodes,
      trains
    ) === null
  );
}

export function getTemplateStationRequirementMessage(
  templateType: TrafficActionType | string | number | null | undefined
): string | null {
  const minimumStationCount = getMinimumStationCountForTemplate(templateType);
  if (minimumStationCount <= 0) {
    return null;
  }

  if (minimumStationCount === 1) {
    return "Välj minst en station för att använda denna mall.";
  }

  return `Välj minst ${minimumStationCount} stationer för att använda denna mall.`;
}

export function getTemplateGenerationRequirementMessage(
  templateType: TrafficActionType | string | number | null | undefined,
  stationCodes: string[] | null | undefined,
  trains: { length: number } | null | undefined = []
): string | null {
  const selectedStationCount =
    stationCodes?.filter((stationCode) => !!stationCode).length ?? 0;
  const minimumStationCount = getMinimumStationCountForTemplate(templateType);

  if (selectedStationCount < minimumStationCount) {
    return getTemplateStationRequirementMessage(templateType);
  }

  if (templateRequiresTrainTrips(templateType) && (trains?.length ?? 0) === 0) {
    return NO_TRIPS_FOUND_MESSAGE;
  }

  return null;
}
